/**
 * Roles utility for role-based access control
 * Centralizes role definitions and checks to improve maintainability
 */

// Role constants
export const ROLES = {
  OPS_ADMIN: "OPS_ADMIN",
  SUPPORT_ADMIN: "SUPPORT_ADMIN",
  FINANCE: "FINANCE",
  CATALOG_MANAGER: "CATALOG_MANAGER",
  SHOP_ADMIN: "SHOP_ADMIN",
  SUPER_ADMIN: "SUPER_ADMIN",
  SHOP_OPS: "SHOP_OPS",
  SHOP_SUPERVISOR: "SHOP_SUPERVISOR",
  SHOP_PICKER: "SHOP_PICKER",
  SHOP_DELIVERY: "SHOP_DELIVERY",
  SHOP_CATALOG_EDITOR: "SHOP_CATALOG_EDITOR",
  SHOP_STOCK_MANAGER: "SHOP_STOCK_MANAGER",
  // Add other roles as needed
};

// Role check functions
export const isOpsAdmin = (role: string): boolean => role === ROLES.OPS_ADMIN;
export const isSupportAdmin = (role: string): boolean =>
  role === ROLES.SUPPORT_ADMIN;
export const isFinance = (role: string): boolean => role === ROLES.FINANCE;
export const isCatalogManager = (role: string): boolean =>
  role === ROLES.CATALOG_MANAGER;
export const isShopAdmin = (role: string): boolean => role === ROLES.SHOP_ADMIN;
export const isShopSupervisor = (role: string): boolean =>
  role === ROLES.SHOP_SUPERVISOR;
export const isShopPicker = (role: string): boolean =>
  role === ROLES.SHOP_PICKER;
export const isShopDelivery = (role: string): boolean =>
  role === ROLES.SHOP_DELIVERY;
export const isShopCatalogEditor = (role: string): boolean =>
  role === ROLES.SHOP_CATALOG_EDITOR;
export const isShopStockManager = (role: string): boolean =>
  role === ROLES.SHOP_STOCK_MANAGER;
export const isSuperAdmin = (role: string): boolean =>
  role === ROLES.SUPER_ADMIN;

// Combined role checks
export const isAdminRole = (role: string): boolean =>
  isOpsAdmin(role) || isSupportAdmin(role) || isSuperAdmin(role);

// Shop user checks - users who belong to a shop
export const isShopUser = (role: string | null | undefined): boolean => {
  if (!role) return false;
  return isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role) || isShopStockManager(role);
};

/**
 * Shop-floor roles that see POS sales-bill sync warnings on Order Details.
 * These are the people who can act on a failed or duplicate POS bill:
 * Shop Admin, Supervisor and Picker.
 */
export const canViewPosOrderSyncWarnings = (
  role: string | null | undefined
): boolean => {
  if (!role) return false;
  return isShopAdmin(role) || isShopSupervisor(role) || isShopPicker(role);
};

// Agent management checks - users who can manage delivery agents
export const canManageAgents = (role: string | null | undefined): boolean => {
  if (!role) return false;
  return isShopAdmin(role) || isShopSupervisor(role);
};

export const isShopOps = (role: string): boolean => role === ROLES.SHOP_OPS;

export const isManagementRole = (role: string): boolean =>
  isOpsAdmin(role) ||
  isSupportAdmin(role) ||
  isFinance(role) ||
  isCatalogManager(role) ||
  isSuperAdmin(role);

// Role-based contact method checks
export const isEmailBasedRole = (role: string | undefined): boolean =>
  role === ROLES.SHOP_SUPERVISOR ||
  role === ROLES.SHOP_PICKER ||
  role === ROLES.SHOP_STOCK_MANAGER;

export const isPhoneBasedRole = (role: string | undefined): boolean =>
  role === ROLES.SHOP_DELIVERY;

// Roles where either email or phone is acceptable (user chooses)
export const isEitherContactRole = (role: string | undefined): boolean =>
  role === ROLES.SHOP_CATALOG_EDITOR;

// Payment status toggle permission checks
/**
 * Check if user can toggle payment status (Picker, Supervisor, Admin)
 * Requirements: 1.1, 2.1, 3.1
 */
export const canTogglePaymentStatus = (role: string): boolean =>
  isShopPicker(role) || isShopSupervisor(role) || isShopAdmin(role);

/**
 * Check if user has supervisor-level permissions for payment status toggle after delivery
 * Only Supervisor and Admin can toggle payment status after delivery (within 24-hour window)
 * Requirements: 2.5, 2.6, 3.2
 */
export const canTogglePaymentStatusAfterDelivery = (role: string): boolean =>
  isShopSupervisor(role) || isShopAdmin(role);

/**
 * Roles that exist on the platform but belong to OTHER applications, not this
 * admin web app. Users who authenticate with one of these roles must be denied
 * login here (they are created from Employee Management for a different app).
 *
 * Blacklist semantics: a role is blocked only if it is listed here. Emptying
 * this set disables the restriction entirely, so login never breaks for roles
 * that are not explicitly blocked.
 */
const BLOCKED_APP_ROLES = new Set<string>([
  ROLES.SHOP_CATALOG_EDITOR,
  ROLES.SHOP_DELIVERY,
]);

/**
 * True when the given role is barred from logging into this admin web app.
 * Returns false for an empty/absent role and whenever the blacklist is empty,
 * so clearing BLOCKED_APP_ROLES safely allows every role to log in.
 */
export const isBlockedFromApp = (role: string | null | undefined): boolean =>
  !!role && BLOCKED_APP_ROLES.has(role);

// You can add more combined checks as needed

// Shop onboarding access check
export const canAccessShopOnboarding = (role: string): boolean =>
  isSuperAdmin(role) || isSupportAdmin(role);

// Subscription access checks
/**
 * Roles that can administer a shop's subscription — creating the subscription
 * link, cancelling, etc. Reached from the Shops table row actions, not the
 * sidebar.
 */
export const canManageSubscriptions = (role: string): boolean =>
  isSuperAdmin(role) || isSupportAdmin(role);

/**
 * Shop-scoped roles that get the Subscription sidebar item for their own shop.
 * Read-only: they see due state and past payments but cannot administer.
 */
export const canViewShopSubscription = (
  role: string | null | undefined
): boolean => {
  if (!role) return false;
  return isShopAdmin(role) || isShopSupervisor(role);
};
